import React, {useCallback, useRef, useState} from 'react';
import {Box, LinearProgress, Typography, useTheme} from '@mui/material';
import {useDropzone, FileWithPath} from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {uploadFileWithProgress, UploadHandle} from '../../services/uploadWithProgress';

interface FileDropZoneProps {
  dir_id: string;
  onUploadStart?: (files: FileWithPath[]) => void;
  onUploadSuccess?: (files: FileWithPath[]) => void;
  onUploadError?: (files: FileWithPath[]) => void;
}

interface FileProgressInfo {
  percent: number;
  status: 'uploading' | 'processing' | 'done' | 'error';
  errorMessage?: string;
}

function getErrorMessage(error: {status: number; data?: any}): string {
  if (error.data?.detail) return error.data.detail;
  if (typeof error.data === 'string' && error.data) return error.data;

  switch (error.status) {
    case 409:
      return 'Datei existiert bereits in diesem Ordner.';
    case 413:
      return 'Datei ist zu groß.';
    case 500:
      return 'Serverfehler beim Hochladen.';
    case 0:
      return 'Netzwerkfehler — Verbindung zum Server fehlgeschlagen.';
    default:
      return `Fehler beim Hochladen (Status ${error.status}).`;
  }
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  dir_id,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
}) => {
  const theme = useTheme();
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileProgress, setFileProgress] = useState<Map<string, FileProgressInfo>>(new Map());
  const abortHandlesRef = useRef<UploadHandle[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      setIsUploading(true);
      onUploadStart?.(acceptedFiles);

      const initialProgress = new Map<string, FileProgressInfo>();
      acceptedFiles.forEach((f) => {
        initialProgress.set(f.name, {percent: 0, status: 'uploading'});
      });
      setFileProgress(new Map(initialProgress));

      let filesProcessed = 0;
      const totalFiles = acceptedFiles.length;
      const handles: UploadHandle[] = [];

      acceptedFiles.forEach((file: FileWithPath) => {
        const handle = uploadFileWithProgress(file, dir_id, (percent, phase) => {
          setFileProgress((prev) => {
            const next = new Map(prev);
            const current = next.get(file.name);
            if (current && (current.status === 'uploading' || current.status === 'processing')) {
              next.set(file.name, {
                ...current,
                percent,
                status: phase === 'processing' ? 'processing' : 'uploading',
              });
            }
            return next;
          });
        });

        handles.push(handle);

        handle.promise
          .then((response: any) => {
            console.log('File uploaded successfully:', response);
            setFileProgress((prev) => {
              const next = new Map(prev);
              next.set(file.name, {percent: 100, status: 'done'});
              return next;
            });
            onUploadSuccess?.([file]);
          })
          .catch((error: {status: number; data?: any}) => {
            const errorMessage = getErrorMessage(error);
            setFileProgress((prev) => {
              const next = new Map(prev);
              next.set(file.name, {
                percent: prev.get(file.name)?.percent ?? 0,
                status: 'error',
                errorMessage,
              });
              return next;
            });
            onUploadError?.([file]);
          })
          .finally(() => {
            filesProcessed++;
            if (filesProcessed === totalFiles) {
              // Only auto-clear if no errors
              setFileProgress((prev) => {
                const anyError = Array.from(prev.values()).some((p) => p.status === 'error');
                if (!anyError) {
                  setTimeout(() => {
                    setIsUploading(false);
                    setFileProgress(new Map());
                    abortHandlesRef.current = [];
                  }, 1500);
                } else {
                  // Keep showing errors, but mark uploading as done so dropzone re-enables
                  setTimeout(() => {
                    setIsUploading(false);
                    abortHandlesRef.current = [];
                  }, 500);
                }
                return prev;
              });
            }
          });
      });

      abortHandlesRef.current = handles;
    },
    [dir_id, onUploadStart, onUploadSuccess, onUploadError]
  );

  const {getRootProps, getInputProps, isDragReject} = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false),
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/pdf': ['.pdf'],
    },
    multiple: true,
    disabled: isUploading,
  });

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: `2px dashed ${
            isDragReject
              ? theme.palette.error.main
              : isDragActive
              ? theme.palette.primary.main
              : theme.palette.grey[400]
          }`,
          borderRadius: 1,
          p: 3,
          m: 2,
          backgroundColor: isDragActive
            ? theme.palette.action.hover
            : theme.palette.background.paper,
          cursor: isUploading ? 'default' : 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': isUploading
            ? {}
            : {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
          opacity: isUploading ? 0.6 : 1,
        }}>
        <input {...getInputProps()} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}>
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: isDragReject
                ? theme.palette.error.main
                : isDragActive
                ? theme.palette.primary.main
                : theme.palette.grey[500],
            }}
          />
          <Typography variant='body1' color='textSecondary' align='center'>
            {isUploading
              ? 'Hochladen...'
              : isDragReject
              ? 'Nur Bilder und PDFs erlaubt!'
              : isDragActive
              ? 'Dateien hier ablegen'
              : 'Dateien hier ablegen oder klicken zum Auswählen'}
          </Typography>
        </Box>
      </Box>

      {fileProgress.size > 0 && (
        <Box sx={{mx: 2, mb: 2}}>
          {Array.from(fileProgress.entries()).map(([fileName, progress]) => {
            const isProcessing = progress.status === 'processing';
            return (
              <Box key={fileName} sx={{mb: 1}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                  {progress.status === 'done' && (
                    <CheckCircleIcon color='success' fontSize='small' />
                  )}
                  {progress.status === 'error' && <ErrorIcon color='error' fontSize='small' />}
                  <Typography variant='body2' noWrap sx={{flex: 1}}>
                    {fileName}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{whiteSpace: 'nowrap'}}>
                    {isProcessing
                      ? 'Verarbeitung...'
                      : progress.status === 'error'
                      ? 'Fehler'
                      : `${progress.percent}%`}
                  </Typography>
                </Box>
                <LinearProgress
                  variant={isProcessing ? 'indeterminate' : 'determinate'}
                  value={isProcessing ? undefined : progress.percent}
                  color={
                    progress.status === 'error'
                      ? 'error'
                      : progress.status === 'done'
                      ? 'success'
                      : 'primary'
                  }
                />
                {progress.status === 'error' && progress.errorMessage && (
                  <Typography variant='caption' color='error' sx={{mt: 0.5, display: 'block'}}>
                    {progress.errorMessage}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
